import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type EventSummary = {
  id: string;
  slug: string;
  title: string;
  titleZh: string;
  venue: string;
  dateLabel: string;
  pendingReviewCount: number;
  approvedCount: number;
  rejectedCount: number;
  checkedInCount: number;
  autoApprove: boolean;
  isActive: boolean;
  registrationOpen: boolean;
};

// --- Labeled Input Components ---

function LabeledInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">
        {label}
        {required ? <span className="text-red-500 ml-1">*</span> : null}
      </span>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
      />
    </label>
  );
}

function LabeledTextarea({
  label,
  value,
  onChange,
  rows,
  required,
}: {
  label: string;
  value?: string;
  onChange: (v: string) => void;
  rows?: number;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-500 mb-1">
        {label}
        {required ? <span className="text-red-500 ml-1">*</span> : null}
      </span>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows ?? 8}
        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
      />
    </label>
  );
}

// --- Crop Modal Component ---

function EventPhotoCropModal({
  imageDataUrl,
  onConfirm,
  onCancel,
}: {
  imageDataUrl: string;
  onConfirm: (dataUrl: string) => void;
  onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });
  const [cropRect, setCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragCorner, setDragCorner] = useState<"br" | "bl" | "tr" | "tl" | "move" | null>(null);
  const dragStart = useRef({ x: 0, y: 0, cx: 0, cy: 0, cw: 0, ch: 0 });

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
      // Locked crop ratio — matches website photo display (aspect-[2/1])
      const cropRatio = 2 / 1;
      let cw: number, ch: number;
      if (img.naturalWidth / img.naturalHeight > cropRatio) {
        ch = Math.round(Math.min(img.naturalHeight * 0.85, img.naturalWidth / cropRatio));
        cw = Math.round(ch * cropRatio);
      } else {
        cw = Math.round(Math.min(img.naturalWidth * 0.85, img.naturalHeight * cropRatio));
        ch = Math.round(cw / cropRatio);
      }
      setCropRect({
        x: Math.round((img.naturalWidth - cw) / 2),
        y: Math.round((img.naturalHeight - ch) / 2),
        w: cw,
        h: ch,
      });
      setImgLoaded(true);
    };
    img.src = imageDataUrl;
  }, [imageDataUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;

    const displayW = canvas.clientWidth;
    const displayH = 400;
    const scale = Math.min(displayW / imgSize.w, displayH / imgSize.h);
    canvas.width = displayW;
    canvas.height = displayH;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, displayW, displayH);
    const drawW = imgSize.w * scale;
    const drawH = imgSize.h * scale;
    const offsetX = (displayW - drawW) / 2;
    const offsetY = (displayH - drawH) / 2;
    ctx.drawImage(img, offsetX, offsetY, drawW, drawH);

    ctx.fillStyle = "rgba(0,0,0,0.45)";
    ctx.fillRect(0, 0, displayW, offsetY + cropRect.y * scale);
    ctx.fillRect(
      0,
      offsetY + (cropRect.y + cropRect.h) * scale,
      displayW,
      displayH - (offsetY + (cropRect.y + cropRect.h) * scale),
    );
    ctx.fillRect(0, offsetY + cropRect.y * scale, offsetX + cropRect.x * scale, cropRect.h * scale);
    ctx.fillRect(
      offsetX + (cropRect.x + cropRect.w) * scale,
      offsetY + cropRect.y * scale,
      displayW - (offsetX + (cropRect.x + cropRect.w) * scale),
      cropRect.h * scale,
    );

    ctx.strokeStyle = "#9B72FF";
    ctx.lineWidth = 2;
    ctx.strokeRect(offsetX + cropRect.x * scale, offsetY + cropRect.y * scale, cropRect.w * scale, cropRect.h * scale);

    const corners: [number, number][] = [
      [offsetX + cropRect.x * scale, offsetY + cropRect.y * scale],
      [offsetX + (cropRect.x + cropRect.w) * scale, offsetY + cropRect.y * scale],
      [offsetX + cropRect.x * scale, offsetY + (cropRect.y + cropRect.h) * scale],
      [offsetX + (cropRect.x + cropRect.w) * scale, offsetY + (cropRect.y + cropRect.h) * scale],
    ];
    ctx.fillStyle = "#9B72FF";
    corners.forEach(([cx, cy]) => {
      ctx.fillRect(cx - 6, cy - 6, 12, 12);
    });
  }, [imgLoaded, imgSize, cropRect]);

  const getCanvasCoords = (e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0, scale: 1, offsetX: 0, offsetY: 0 };
    const rect = canvas.getBoundingClientRect();
    const displayW = canvas.clientWidth;
    const displayH = 400;
    const scale = Math.min(displayW / imgSize.w, displayH / imgSize.h);
    const drawW = imgSize.w * scale;
    const drawH = imgSize.h * scale;
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      scale,
      offsetX: (displayW - drawW) / 2,
      offsetY: (displayH - drawH) / 2,
    };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const { x, y, scale, offsetX, offsetY } = getCanvasCoords(e);
    const cx = offsetX + cropRect.x * scale;
    const cy = offsetY + cropRect.y * scale;
    const cw = cropRect.w * scale;
    const ch = cropRect.h * scale;
    const cornerSize = 14;
    let corner: "br" | "bl" | "tr" | "tl" | "move" | null = null;
    if (Math.abs(x - cx) < cornerSize && Math.abs(y - cy) < cornerSize) corner = "tl";
    else if (Math.abs(x - (cx + cw)) < cornerSize && Math.abs(y - cy) < cornerSize) corner = "tr";
    else if (Math.abs(x - cx) < cornerSize && Math.abs(y - (cy + ch)) < cornerSize) corner = "bl";
    else if (Math.abs(x - (cx + cw)) < cornerSize && Math.abs(y - (cy + ch)) < cornerSize) corner = "br";
    else if (x > cx && x < cx + cw && y > cy && y < cy + ch) corner = "move";
    if (!corner) return;
    setDragCorner(corner);
    setDragging(true);
    dragStart.current = { x, y, cx: cropRect.x, cy: cropRect.y, cw: cropRect.w, ch: cropRect.h };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !dragCorner) return;
    const { x, y, scale } = getCanvasCoords(e);
    const dx = Math.round((x - dragStart.current.x) / scale);
    const dy = Math.round((y - dragStart.current.y) / scale);
    const { cx, cy, cw, ch } = dragStart.current;
    const cropRatio = 2 / 1;
    const minW = 80;
    const minH = Math.round(minW / cropRatio);

    let nx = cx,
      ny = cy,
      nw = cw,
      nh = ch;

    switch (dragCorner) {
      case "move":
        nx = Math.max(0, Math.min(imgSize.w - cw, cx + dx));
        ny = Math.max(0, Math.min(imgSize.h - ch, cy + dy));
        nw = cw;
        nh = ch;
        break;
      case "br":
        nw = Math.max(minW, Math.min(imgSize.w - cx, cw + dx));
        nh = Math.round(nw / cropRatio);
        if (cy + nh > imgSize.h) {
          nh = imgSize.h - cy;
          nw = Math.round(nh * cropRatio);
        }
        nx = cx;
        ny = cy;
        break;
      case "bl":
        nw = Math.max(minW, cw - dx);
        nh = Math.round(nw / cropRatio);
        nx = Math.max(0, Math.min(cx + (cw - nw), imgSize.w - nw));
        if (cy + nh > imgSize.h) {
          nh = imgSize.h - cy;
          nw = Math.round(nh * cropRatio);
          nx = Math.max(0, Math.min(cx + (cw - nw), imgSize.w - nw));
        }
        ny = cy;
        break;
      case "tr":
        nh = Math.max(minH, Math.min(imgSize.h - cy, ch - dy));
        nw = Math.round(nh * cropRatio);
        if (cx + nw > imgSize.w) {
          nw = imgSize.w - cx;
          nh = Math.round(nw / cropRatio);
        }
        nx = cx;
        ny = Math.max(0, Math.min(cy + (ch - nh), imgSize.h - nh));
        break;
      case "tl":
        nh = Math.max(minH, Math.min(imgSize.h - cy, ch - dy));
        nw = Math.round(nh * cropRatio);
        nx = Math.max(0, Math.min(cx + (cw - nw), imgSize.w - nw));
        ny = Math.max(0, Math.min(cy + (ch - nh), imgSize.h - nh));
        if (cy + nh > imgSize.h) {
          nh = imgSize.h - cy;
          nw = Math.round(nh * cropRatio);
        }
        break;
    }

    setCropRect({ x: nx, y: ny, w: nw, h: nh });
  };

  const handleMouseUp = () => {
    setDragging(false);
    setDragCorner(null);
  };

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    const canvas = document.createElement("canvas");
    canvas.width = cropRect.w;
    canvas.height = cropRect.h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, cropRect.x, cropRect.y, cropRect.w, cropRect.h, 0, 0, cropRect.w, cropRect.h);
    onConfirm(canvas.toDataURL("image/jpeg", 0.9));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={onCancel}>
      <div
        className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900">Crop Photo / 裁剪照片</p>
            <p className="text-xs text-gray-500">
              Drag corners to resize. Drag inside to reposition. / 拖曳角落調整大小，拖曳內部移動位置。
            </p>
          </div>
          <button
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
          >
            &times;
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100">
          {!imgLoaded ? (
            <p className="text-gray-400">Loading... / 載入中...</p>
          ) : (
            <canvas
              ref={canvasRef}
              className="max-w-full cursor-crosshair rounded-2xl"
              style={{ width: "100%", height: 400, touchAction: "none" }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          )}
        </div>
        <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            Cancel / 取消
          </button>
          <button
            onClick={handleCrop}
            disabled={!imgLoaded}
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-40"
          >
            Crop & Save / 裁剪並儲存
          </button>
        </div>
      </div>
    </div>
  );
}

type TicketFormEntry = {
  code: string;
  name: string;
  nameZh: string;
  genderLabel: string;
  priceHkd: number;
  priceLabel: string;
  includes: string;
  includesZh: string;
  bookingChannel: string;
  badge: string;
  isActive: boolean;
  soldOut: boolean;
  ticketLimit: number;
  currentSold: number;
};

type DiscountCodeEntry = {
  id: string;
  code: string;
  discountType: string;
  discountValue: number;
  maxUses: number;
  currentUses: number;
  isActive: boolean;
};

type CustomFieldEntry = {
  id: string;
  fieldKey: string;
  labelEn: string;
  labelZh: string;
  fieldType: string;
  options: { en: string; zh: string }[];
  required: boolean;
  sortOrder: number;
  isActive: boolean;
  parentFieldKey: string | null;
  parentValue: string | null;
};

type PaymentMethodEntry = {
  id?: string;
  name: string;
  description: string;
  photoDataUrl: string;
};

const tokenStorageKey = "aurays_admin_token";

function AlertModal({
  title,
  subtitle,
  message,
  onClose,
}: {
  title: string;
  subtitle: string;
  message: string;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <p className="font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
          >
            &times;
          </button>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center rounded-full bg-red-100 text-red-500 text-sm font-bold shrink-0">
              !
            </span>
            <p className="text-sm text-gray-700 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="border-t border-gray-100 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            autoFocus
            className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:bg-gray-700 transition-colors cursor-pointer"
          >
            OK / 知道了
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EventAdminPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [eventSummaries, setEventSummaries] = useState<EventSummary[]>([]);
  const [eventManagement, setEventManagement] = useState<{
    editingEvent: EventSummary | null;
    editForm: Record<string, string>;
    isEditing: boolean;
  }>({ editingEvent: null, editForm: {}, isEditing: false });
  const [editFaqs, setEditFaqs] = useState<{ question: string; answer: string }[]>([]);
  const [editTickets, setEditTickets] = useState<TicketFormEntry[]>([]);
  const [editPaymentMethods, setEditPaymentMethods] = useState<PaymentMethodEntry[]>([]);
  const [discountCodes, setDiscountCodes] = useState<DiscountCodeEntry[]>([]);
  const [newDiscount, setNewDiscount] = useState({ code: "", discountType: "fixed", discountValue: "", maxUses: "" });
  const [discountSaving, setDiscountSaving] = useState(false);

  const [customFields, setCustomFields] = useState<CustomFieldEntry[]>([]);
  const [newCustomField, setNewCustomField] = useState({
    fieldKey: "",
    labelEn: "",
    labelZh: "",
    fieldType: "text",
    options: [{ en: "", zh: "" }],
    required: false,
    parentFieldKey: "",
    parentValue: "",
  });
  const [cfSaving, setCfSaving] = useState(false);
  const [showCustomFieldValidation, setShowCustomFieldValidation] = useState(false);
  const [editingCfId, setEditingCfId] = useState<string | null>(null);
  const [editCfForm, setEditCfForm] = useState<{
    fieldKey: string;
    labelEn: string;
    labelZh: string;
    fieldType: string;
    options: { en: string; zh: string }[];
    required: boolean;
    parentFieldKey: string;
    parentValue: string;
  }>({
    fieldKey: "",
    labelEn: "",
    labelZh: "",
    fieldType: "text",
    options: [{ en: "", zh: "" }],
    required: false,
    parentFieldKey: "",
    parentValue: "",
  });
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [importModal, setImportModal] = useState(false);
  const [importJson, setImportJson] = useState<string>("");
  const [importSlug, setImportSlug] = useState("");
  const [importError, setImportError] = useState("");

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") resolve(reader.result);
        else reject(new Error("invalid_reader_result"));
      };
      reader.onerror = () => reject(new Error("file_read_failed"));
      reader.readAsDataURL(file);
    });

  const authHeaders = useMemo(() => {
    const t = token.trim();
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, [token]);

  useEffect(() => {
    const savedToken = sessionStorage.getItem(tokenStorageKey);
    if (savedToken) {
      setToken(savedToken);
      setAuthed(true);
    }
  }, []);

  const loadEventSummaries = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/events", { headers: authHeaders });
      if (!res.ok) throw new Error("Request failed");
      const data = (await res.json()) as { ok?: boolean; rows?: EventSummary[] };
      if (!data.ok || !Array.isArray(data.rows)) throw new Error("Request failed");
      setEventSummaries(data.rows);
    } catch {
      setError("Failed to load events. / 無法載入活動。");
    }
  }, [authHeaders]);

  useEffect(() => {
    if (!authed || !token.trim()) return;
    void loadEventSummaries();
  }, [authed, token, loadEventSummaries]);

  const openEventEditor = useCallback(
    async (event?: EventSummary) => {
      if (event) {
        setLoading(true);
        try {
          const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}`, {
            headers: authHeaders,
          });
          const data = (await res.json()) as { ok?: boolean; event?: Record<string, unknown> } | null;
          const full = data?.event;
          if (full) {
            setEventManagement({
              editingEvent: event,
              editForm: {
                slug: String(full.slug || ""),
                title: String(full.title || ""),
                titleZh: String(full.titleZh || ""),
                shortCode: String(full.shortCode || ""),
                heroTitle: String(full.heroTitle || ""),
                heroSubtitle: String(full.heroSubtitle || ""),
                summary: String(full.summary || ""),
                memberPrivilege: String(full.memberPrivilege || ""),
                venue: String(full.venue || ""),
                address: String(full.address || ""),
                organiserContact: String(full.organiserContact || ""),
                dateLabel: String(full.dateLabel || ""),
                timeLabel: String(full.timeLabel || ""),
                doorPolicy: String(full.doorPolicy || ""),
                entryAgePolicy: String(full.entryAgePolicy || ""),
                dressCode: String(full.dressCode || ""),
                cancellationPolicy: String(full.cancellationPolicy || ""),
                transferPolicy: String(full.transferPolicy || ""),
                weatherPolicy: String(full.weatherPolicy || ""),
                discountCodePolicy: String(full.discountCodePolicy || ""),
                rightOfAdmission: String(full.rightOfAdmission || ""),
                emailNotificationNote: String(full.emailNotificationNote || ""),
                highlights: Array.isArray(full.highlights) ? full.highlights.join("\n") : String(full.highlights || ""),
                autoApprove: String(!!full.autoApprove),
                isActive: String(full.isActive !== false),
                showShortCode: String(full.showShortCode !== false),
                registrationOpen: String(full.registrationOpen !== false),
                photoDataUrl: typeof full.photoDataUrl === "string" ? full.photoDataUrl : "",
              },
              isEditing: true,
            });
            if (Array.isArray(full.faqs)) {
              setEditFaqs(full.faqs as { question: string; answer: string }[]);
            } else {
              setEditFaqs([]);
            }
            if (Array.isArray(full.ticketTypes)) {
              setEditTickets(full.ticketTypes as TicketFormEntry[]);
            } else {
              setEditTickets([]);
            }
            if (Array.isArray(full.paymentMethods)) {
              setEditPaymentMethods(
                (
                  full.paymentMethods as {
                    id?: string;
                    name?: string;
                    description?: string;
                    photoDataUrl?: string;
                  }[]
                ).map((m) => ({
                  id: m.id,
                  name: String(m.name || ""),
                  description: String(m.description || ""),
                  photoDataUrl: typeof m.photoDataUrl === "string" ? m.photoDataUrl : "",
                })),
              );
            } else {
              setEditPaymentMethods([]);
            }
            // Load discount codes
            try {
              const dcRes = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/discount-codes`, {
                headers: authHeaders,
              });
              const dcData = (await dcRes.json()) as { ok?: boolean; codes?: DiscountCodeEntry[] } | null;
              if (dcData?.ok && Array.isArray(dcData.codes)) {
                setDiscountCodes(dcData.codes);
              } else {
                setDiscountCodes([]);
              }
            } catch {
              setDiscountCodes([]);
            }
            // Load custom fields
            try {
              const cfRes = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/custom-fields`, {
                headers: authHeaders,
              });
              const cfData = (await cfRes.json()) as { ok?: boolean; fields?: CustomFieldEntry[] } | null;
              if (cfData?.ok && Array.isArray(cfData.fields)) {
                setCustomFields(cfData.fields);
              } else {
                setCustomFields([]);
              }
            } catch {
              setCustomFields([]);
            }
            return;
          }
        } catch {
          // fallback to basic fields
        } finally {
          setLoading(false);
        }
      }

      // New event or fallback
      setEditFaqs([]);
      setEditTickets([]);
      setEditPaymentMethods([]);
      setDiscountCodes([]);
      setCustomFields([]);
      setEventManagement({
        editingEvent: null,
        editForm: {
          slug: "",
          title: "",
          titleZh: "",
          shortCode: "",
          heroTitle: "",
          heroSubtitle: "",
          summary: "",
          memberPrivilege: "",
          venue: "",
          address: "",
          organiserContact: "",
          dateLabel: "",
          timeLabel: "",
          doorPolicy: "",
          entryAgePolicy: "",
          dressCode: "",
          cancellationPolicy: "",
          transferPolicy: "",
          weatherPolicy: "",
          discountCodePolicy: "",
          rightOfAdmission: "",
          emailNotificationNote: "",
          highlights: "",
          autoApprove: "false",
          isActive: "true",
          showShortCode: "true",
          registrationOpen: "true",
          photoDataUrl: "",
        },
        isEditing: true,
      });
    },
    [authHeaders],
  );

  const saveEvent = useCallback(async () => {
    const form = eventManagement.editForm;
    const eventId = eventManagement.editingEvent?.id;
    if (!form.slug || !form.title) {
      setError("");
      setSaveError("Slug and title are required. / 標題和 Slug 必填。");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const body: Record<string, unknown> = {
        slug: form.slug,
        title: form.title,
        titleZh: form.titleZh,
        shortCode: form.shortCode,
        heroTitle: form.heroTitle,
        heroSubtitle: form.heroSubtitle,
        summary: form.summary,
        memberPrivilege: form.memberPrivilege,
        venue: form.venue,
        address: form.address,
        organiserContact: form.organiserContact,
        dateLabel: form.dateLabel,
        timeLabel: form.timeLabel,
        doorPolicy: form.doorPolicy,
        entryAgePolicy: form.entryAgePolicy,
        dressCode: form.dressCode,
        cancellationPolicy: form.cancellationPolicy,
        transferPolicy: form.transferPolicy,
        weatherPolicy: form.weatherPolicy,
        discountCodePolicy: form.discountCodePolicy,
        rightOfAdmission: form.rightOfAdmission,
        emailNotificationNote: form.emailNotificationNote,
        autoApprove: form.autoApprove === "true",
        isActive: form.isActive !== "false",
        showShortCode: form.showShortCode !== "false",
        registrationOpen: form.registrationOpen !== "false",
        highlights: ((form.highlights as string) || "")
          .split("\n")
          .map((s: string) => s.trim())
          .filter(Boolean),
        ticketTypes: editTickets,
        faqs: editFaqs,
        photoDataUrl: (form.photoDataUrl as string) || "",
        paymentMethods: editPaymentMethods.map((m) => ({
          name: m.name,
          description: m.description,
          photoDataUrl: m.photoDataUrl || "",
        })),
      };

      const url = eventId ? `/api/admin/events/${encodeURIComponent(eventId)}` : "/api/admin/events";
      const method = eventId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "save_failed");
      }

      setEventManagement({ editingEvent: null, editForm: {}, isEditing: false });
      await loadEventSummaries();
    } catch (err) {
      setError("");
      setSaveError(err instanceof Error ? err.message : "Failed to save event. / 儲存活動失敗。");
    } finally {
      setLoading(false);
    }
  }, [
    authHeaders,
    eventManagement.editForm,
    eventManagement.editingEvent?.id,
    loadEventSummaries,
    editFaqs,
    editTickets,
    editPaymentMethods,
  ]);

  const toggleEventActive = useCallback(
    async (event: EventSummary) => {
      if (
        !window.confirm(
          `${event.isActive ? "Deactivate" : "Activate"} event "${event.titleZh}"?\n\n確定要${event.isActive ? "停用" : "啟用"}活動「${event.titleZh}」？`,
        )
      )
        return;
      try {
        const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/toggle-active`, {
          method: "POST",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (!res.ok || !data?.ok) throw new Error("toggle_failed");
        await loadEventSummaries();
      } catch {
        setError("Failed to toggle event. / 切換活動狀態失敗。");
      }
    },
    [authHeaders, loadEventSummaries],
  );

  const toggleEventAutoApprove = useCallback(
    async (event: EventSummary) => {
      if (
        !window.confirm(
          `${event.autoApprove ? "Disable" : "Enable"} auto-approve for "${event.titleZh}"?\n\n確定要${event.autoApprove ? "關閉" : "開啟"}「${event.titleZh}」的自動審核？`,
        )
      )
        return;
      try {
        const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/toggle-auto-approve`, {
          method: "POST",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (!res.ok || !data?.ok) throw new Error("toggle_failed");
        await loadEventSummaries();
      } catch {
        setError("Failed to toggle auto-approve. / 切換自動審核失敗。");
      }
    },
    [authHeaders, loadEventSummaries],
  );

  const toggleEventRegistration = useCallback(
    async (event: EventSummary) => {
      if (
        !window.confirm(
          `${event.registrationOpen ? "Close" : "Reopen"} registration for "${event.titleZh}"?\n\n確定要${event.registrationOpen ? "截止" : "重新開放"}「${event.titleZh}」的報名？`,
        )
      )
        return;
      try {
        const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}/toggle-registration`, {
          method: "POST",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (!res.ok || !data?.ok) throw new Error("toggle_failed");
        await loadEventSummaries();
      } catch {
        setError("Failed to toggle registration. / 切換報名狀態失敗。");
      }
    },
    [authHeaders, loadEventSummaries],
  );

  const handleAddDiscountCode = useCallback(async () => {
    const eventId = eventManagement.editingEvent?.id;
    if (!eventId) return;
    const code = newDiscount.code.trim().toUpperCase();
    const discountValue = Number(newDiscount.discountValue);
    if (!code || discountValue <= 0) {
      setError("Please enter a valid code and discount value. / 請輸入有效的折扣碼及金額。");
      return;
    }
    setDiscountSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/events/${encodeURIComponent(eventId)}/discount-codes`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          discountType: newDiscount.discountType,
          discountValue,
          maxUses: Number(newDiscount.maxUses) || 0,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; code?: DiscountCodeEntry; error?: string };
      if (!res.ok || !data?.ok) throw new Error(data?.error || "failed");
      if (data.code) setDiscountCodes((prev) => [...prev, data.code!]);
      setNewDiscount({ code: "", discountType: "fixed", discountValue: "", maxUses: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add discount code / 新增折扣碼失敗");
    } finally {
      setDiscountSaving(false);
    }
  }, [authHeaders, eventManagement.editingEvent?.id, newDiscount]);

  const handleToggleDiscountCode = useCallback(
    async (dc: DiscountCodeEntry) => {
      const eventId = eventManagement.editingEvent?.id;
      if (!eventId) return;
      try {
        const res = await fetch(
          `/api/admin/events/${encodeURIComponent(eventId)}/discount-codes/${encodeURIComponent(dc.id)}`,
          {
            method: "PUT",
            headers: { ...authHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({ isActive: !dc.isActive }),
          },
        );
        const data = (await res.json()) as { ok?: boolean; code?: DiscountCodeEntry };
        if (!res.ok || !data?.ok) throw new Error("failed");
        setDiscountCodes((prev) => prev.map((c) => (c.id === dc.id && data.code ? data.code : c)));
      } catch {
        setError("Failed to toggle discount code / 切換折扣碼失敗");
      }
    },
    [authHeaders, eventManagement.editingEvent?.id],
  );

  const handleDeleteDiscountCode = useCallback(
    async (dc: DiscountCodeEntry) => {
      const eventId = eventManagement.editingEvent?.id;
      if (!eventId) return;
      if (!window.confirm(`Delete discount code "${dc.code}"? / 刪除折扣碼「${dc.code}」？`)) return;
      try {
        const res = await fetch(
          `/api/admin/events/${encodeURIComponent(eventId)}/discount-codes/${encodeURIComponent(dc.id)}`,
          {
            method: "DELETE",
            headers: authHeaders,
          },
        );
        const data = (await res.json()) as { ok?: boolean };
        if (!res.ok || !data?.ok) throw new Error("failed");
        setDiscountCodes((prev) => prev.filter((c) => c.id !== dc.id));
      } catch {
        setError("Failed to delete discount code / 刪除折扣碼失敗");
      }
    },
    [authHeaders, eventManagement.editingEvent?.id],
  );

  const handleAddCustomField = useCallback(async () => {
    const eventId = eventManagement.editingEvent?.id;
    if (!eventId) return;
    const { fieldKey, labelEn, labelZh, fieldType, options, required, parentFieldKey, parentValue } = newCustomField;
    if (!fieldKey.trim() || !labelEn.trim()) {
      setShowCustomFieldValidation(true);
      setError("Please enter fieldKey and labelEn. / 請填寫 fieldKey 和 Label (EN)。");
      return;
    }
    setCfSaving(true);
    setError("");
    try {
      const res = await fetch(`/api/admin/events/${encodeURIComponent(eventId)}/custom-fields`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          fieldKey: fieldKey.trim(),
          labelEn: labelEn.trim(),
          labelZh: labelZh.trim(),
          fieldType,
          options: fieldType === "dropdown" ? options.filter((o) => o.en) : [],
          required,
          sortOrder: customFields.length,
          parentFieldKey: parentFieldKey.trim() || null,
          parentValue: parentValue.trim() || null,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; field?: CustomFieldEntry; error?: string };
      if (!res.ok || !data?.ok) throw new Error(data?.error || "failed");
      if (data.field) setCustomFields((prev) => [...prev, data.field!]);
      setShowCustomFieldValidation(false);
      setNewCustomField({
        fieldKey: "",
        labelEn: "",
        labelZh: "",
        fieldType: "text",
        options: [{ en: "", zh: "" }],
        required: false,
        parentFieldKey: "",
        parentValue: "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add custom field / 新增欄位失敗");
    } finally {
      setCfSaving(false);
    }
  }, [authHeaders, eventManagement.editingEvent?.id, newCustomField, customFields.length]);

  const handleDeleteCustomField = useCallback(
    async (cf: CustomFieldEntry) => {
      const eventId = eventManagement.editingEvent?.id;
      if (!eventId) return;
      if (!window.confirm(`Delete field "${cf.labelEn}"? / 刪除欄位「${cf.labelEn}」？`)) return;
      try {
        const res = await fetch(
          `/api/admin/events/${encodeURIComponent(eventId)}/custom-fields/${encodeURIComponent(cf.id)}`,
          {
            method: "DELETE",
            headers: authHeaders,
          },
        );
        const data = (await res.json()) as { ok?: boolean };
        if (!res.ok || !data?.ok) throw new Error("failed");
        setCustomFields((prev) => prev.filter((f) => f.id !== cf.id));
      } catch {
        setError("Failed to delete custom field / 刪除欄位失敗");
      }
    },
    [authHeaders, eventManagement.editingEvent?.id],
  );

  const handleToggleCustomField = useCallback(
    async (cf: CustomFieldEntry) => {
      const eventId = eventManagement.editingEvent?.id;
      if (!eventId) return;
      try {
        const res = await fetch(
          `/api/admin/events/${encodeURIComponent(eventId)}/custom-fields/${encodeURIComponent(cf.id)}`,
          {
            method: "PUT",
            headers: { ...authHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({ isActive: !cf.isActive }),
          },
        );
        const data = (await res.json()) as { ok?: boolean; field?: CustomFieldEntry };
        if (!res.ok || !data?.ok) throw new Error("failed");
        setCustomFields((prev) => prev.map((f) => (f.id === cf.id && data.field ? data.field : f)));
      } catch {
        setError("Failed to toggle custom field / 切換欄位失敗");
      }
    },
    [authHeaders, eventManagement.editingEvent?.id],
  );

  const handleSaveCustomField = useCallback(
    async (cf: CustomFieldEntry) => {
      const eventId = eventManagement.editingEvent?.id;
      if (!eventId) return;
      const { fieldKey, labelEn, labelZh, fieldType, options, required, parentFieldKey, parentValue } = editCfForm;
      if (!fieldKey.trim() || !labelEn.trim()) {
        setError("Please enter fieldKey and labelEn. / 請填寫 fieldKey 和 Label (EN)。");
        return;
      }
      setCfSaving(true);
      setError("");
      try {
        const res = await fetch(
          `/api/admin/events/${encodeURIComponent(eventId)}/custom-fields/${encodeURIComponent(cf.id)}`,
          {
            method: "PUT",
            headers: { ...authHeaders, "Content-Type": "application/json" },
            body: JSON.stringify({
              fieldKey: fieldKey.trim(),
              labelEn: labelEn.trim(),
              labelZh: labelZh.trim(),
              fieldType,
              options: fieldType === "dropdown" ? options.filter((o) => o.en) : [],
              required,
              parentFieldKey: parentFieldKey.trim() || null,
              parentValue: parentValue.trim() || null,
            }),
          },
        );
        const data = (await res.json()) as { ok?: boolean; field?: CustomFieldEntry; error?: string };
        if (!res.ok || !data?.ok) throw new Error(data?.error || "failed");
        if (data.field) {
          setCustomFields((prev) => prev.map((f) => (f.id === cf.id ? data.field! : f)));
        }
        setEditingCfId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save custom field / 儲存欄位失敗");
      } finally {
        setCfSaving(false);
      }
    },
    [authHeaders, eventManagement.editingEvent?.id, editCfForm],
  );

  const deleteEvent = useCallback(
    async (event: EventSummary) => {
      if (!window.confirm(`Delete event "${event.titleZh}"? This will also delete all registrations.`)) return;
      try {
        const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}`, {
          method: "DELETE",
          headers: authHeaders,
        });
        const data = (await res.json().catch(() => null)) as { ok?: boolean } | null;
        if (!res.ok || !data?.ok) throw new Error("delete_failed");
        await loadEventSummaries();
      } catch {
        setError("Failed to delete event. / 刪除活動失敗。");
      }
    },
    [authHeaders, loadEventSummaries],
  );

  const exportEvent = useCallback(
    async (event: EventSummary) => {
      try {
        setError("");
        const res = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}`, { headers: authHeaders });
        const data = (await res.json()) as { ok?: boolean; event?: Record<string, unknown> } | null;
        const full = data?.event;
        if (!full) throw new Error("fetch_failed");
        // Clean export: remove server-only fields, keep data URLs
        const { photoUrl, ...clean } = full as Record<string, unknown>;
        const blob = new Blob([JSON.stringify(clean, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${event.slug || event.id}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        setError("Failed to export event. / 匯出活動失敗。");
      }
    },
    [authHeaders],
  );

  const handleImportFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    (e.target as HTMLInputElement).value = "";
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (!parsed.slug && !parsed.title) throw new Error("invalid");
      setImportJson(JSON.stringify(parsed, null, 2));
      setImportSlug(String(parsed.slug || ""));
      setImportError("");
      setImportModal(true);
    } catch {
      setError("Invalid event JSON file. / 無效的活動 JSON 檔案。");
    }
  }, []);

  const importEvent = useCallback(async () => {
    try {
      const body = JSON.parse(importJson) as Record<string, unknown>;
      if (!body.title || !importSlug) {
        setImportError("Title and slug are required. / 標題和 Slug 必填。");
        return;
      }
      body.slug = importSlug;
      // Normalize highlights: exported JSON may have it as a JSON string or array
      if (typeof body.highlights === "string") {
        try {
          body.highlights = JSON.parse(body.highlights);
        } catch {
          body.highlights = [];
        }
      }
      if (!Array.isArray(body.highlights)) body.highlights = [];
      // Remove server-only fields that shouldn't be in the create payload
      delete body.id;
      delete body.created_at;
      delete body.updated_at;

      setLoading(true);
      setImportError("");
      const res = await fetch("/api/admin/events", {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = (await res.json().catch(() => null)) as { ok?: boolean; error?: string } | null;
      if (!res.ok || !data?.ok) throw new Error(data?.error || "import_failed");
      setImportModal(false);
      setImportJson("");
      setImportSlug("");
      await loadEventSummaries();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Failed to import event. / 匯入活動失敗。");
    } finally {
      setLoading(false);
    }
  }, [importJson, importSlug, authHeaders, loadEventSummaries]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />
      <section className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-white border border-gray-100 rounded-3xl p-6 md:p-8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Event Management / 活動管理</h1>
                <p className="text-sm text-gray-500">
                  Create, edit, toggle active, auto-approve, and delete events. / 建立、編輯、啟用、自動審核和刪除活動。
                </p>
              </div>
            </div>

            {!authed ? (
              <div className="max-w-md">
                <p className="text-sm text-gray-500">
                  Please sign in via{" "}
                  <a href="/aurays-admin" className="text-[#7C55CC] hover:underline">
                    Aurays Admin
                  </a>{" "}
                  first.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Your Events / 你的活動</p>
                  </div>
                  <div className="flex gap-2">
                    <label className="px-4 py-2 rounded-full text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
                      <input type="file" accept=".json" className="hidden" onChange={handleImportFile} />
                      Import / 匯入
                    </label>
                    <button
                      onClick={() => openEventEditor()}
                      className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700 transition-colors"
                    >
                      New Event / 新增活動
                    </button>
                  </div>
                </div>

                {eventManagement.isEditing ? (
                  <>
                    <div className="bg-white rounded-3xl border border-gray-100 p-6 md:p-8 space-y-4">
                      <h3 className="text-lg font-bold text-gray-900">
                        {eventManagement.editingEvent ? "Edit Event / 編輯活動" : "New Event / 新增活動"}
                      </h3>

                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-2">Event Photo / 活動照片</label>
                        <label className="flex items-center justify-center border border-dashed border-gray-300 rounded-2xl px-4 py-5 text-sm text-gray-500 cursor-pointer hover:border-[#9B72FF] hover:text-gray-700 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (!file.type.startsWith("image/")) {
                                setUploadError("Please upload an image file. / 請上傳圖片檔案。");
                                return;
                              }
                              if (file.size > 5 * 1024 * 1024) {
                                setUploadError("Image must be 5MB or smaller. / 圖片不得超過 5MB。");
                                return;
                              }
                              try {
                                const dataUrl = await readFileAsDataUrl(file);
                                setUploadError("");
                                setCropImage(dataUrl);
                              } catch {
                                setUploadError("Failed to read image. / 讀取圖片失敗。");
                              }
                              (e.target as HTMLInputElement).value = "";
                            }}
                          />
                          {eventManagement.editForm.photoDataUrl
                            ? "Change photo / 更換照片"
                            : "Upload photo / 上傳照片"}
                        </label>
                        {eventManagement.editForm.photoDataUrl ? (
                          <div className="mt-3 rounded-2xl border border-gray-200 overflow-hidden">
                            <img
                              src={eventManagement.editForm.photoDataUrl}
                              alt="Event photo preview"
                              className="w-full aspect-[2/1] object-cover bg-gray-50"
                            />
                          </div>
                        ) : null}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-800 mb-1">
                          Payment Methods / 付款方式
                        </label>
                        <p className="text-xs text-gray-500 mb-3">
                          Registration will ask the attendee to pick one method and show its own description &amp;
                          photo. / 報名時會請參加者選擇一種付款方式，並顯示對應的說明及圖片。
                        </p>

                        {editPaymentMethods.map((pm, idx) => (
                          <div
                            key={pm.id || `pm-${idx}`}
                            className="border border-gray-200 rounded-2xl p-4 mb-3 space-y-3 bg-gray-50/50"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                                Method {idx + 1} / 付款方式 {idx + 1}
                              </span>
                              <button
                                type="button"
                                onClick={() => setEditPaymentMethods((prev) => prev.filter((_, i) => i !== idx))}
                                className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                              >
                                Remove / 移除
                              </button>
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Name / 付款方式名稱
                              </label>
                              <input
                                value={pm.name}
                                onChange={(e) =>
                                  setEditPaymentMethods((prev) =>
                                    prev.map((item, i) => (i === idx ? { ...item, name: e.target.value } : item)),
                                  )
                                }
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                placeholder="e.g. FPS / 轉數快"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Description / 付款說明
                              </label>
                              <textarea
                                value={pm.description}
                                onChange={(e) =>
                                  setEditPaymentMethods((prev) =>
                                    prev.map((item, i) =>
                                      i === idx ? { ...item, description: e.target.value } : item,
                                    ),
                                  )
                                }
                                rows={3}
                                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                placeholder="Payment details shown when this method is selected (EN + 中文 in one text) / 選擇此方式時顯示的付款詳情"
                              />
                            </div>

                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                Photo (optional) / 圖片（可選）
                              </label>
                              <div className="flex items-center gap-3">
                                <label className="flex items-center justify-center border border-dashed border-gray-300 rounded-2xl px-4 py-3 text-xs text-gray-500 cursor-pointer hover:border-[#9B72FF] hover:text-gray-700 transition-colors shrink-0">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      if (!file.type.startsWith("image/")) {
                                        setUploadError("Please upload an image file. / 請上傳圖片檔案。");
                                        return;
                                      }
                                      if (file.size > 5 * 1024 * 1024) {
                                        setUploadError("Image must be 5MB or smaller. / 圖片不得超過 5MB。");
                                        return;
                                      }
                                      try {
                                        const dataUrl = await readFileAsDataUrl(file);
                                        setUploadError("");
                                        setEditPaymentMethods((prev) =>
                                          prev.map((item, i) =>
                                            i === idx ? { ...item, photoDataUrl: dataUrl } : item,
                                          ),
                                        );
                                      } catch {
                                        setUploadError("Failed to read image. / 讀取圖片失敗。");
                                      }
                                      (e.target as HTMLInputElement).value = "";
                                    }}
                                  />
                                  {pm.photoDataUrl ? "Change / 更換" : "Upload / 上傳"}
                                </label>
                                {pm.photoDataUrl ? (
                                  <>
                                    <img
                                      src={pm.photoDataUrl}
                                      alt="Payment method preview"
                                      className="h-20 w-20 rounded-xl object-cover border border-gray-200 bg-white"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setEditPaymentMethods((prev) =>
                                          prev.map((item, i) => (i === idx ? { ...item, photoDataUrl: "" } : item)),
                                        )
                                      }
                                      className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                                    >
                                      Remove / 移除圖片
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={() =>
                            setEditPaymentMethods((prev) => [
                              ...prev,
                              { id: undefined, name: "", description: "", photoDataUrl: "" },
                            ])
                          }
                          className="w-full border border-dashed border-gray-300 rounded-2xl px-4 py-3 text-sm text-gray-500 hover:border-[#9B72FF] hover:text-gray-700 transition-colors cursor-pointer"
                        >
                          + Add payment method / 新增付款方式
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <LabeledInput
                          label="Slug / 網址代號"
                          value={eventManagement.editForm.slug}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, slug: v } }))
                          }
                          required
                        />
                        <LabeledInput
                          label="Chinese Title / 活動名稱"
                          value={eventManagement.editForm.titleZh}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, titleZh: v } }))
                          }
                          required
                        />
                        <LabeledInput
                          label="English Title / 英文名稱"
                          value={eventManagement.editForm.title}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, title: v } }))
                          }
                          required
                        />
                        <LabeledInput
                          label="Short Code / 簡碼"
                          value={eventManagement.editForm.shortCode}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, shortCode: v } }))
                          }
                        />
                        <LabeledInput
                          label="Hero Title / 主標題"
                          value={eventManagement.editForm.heroTitle}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, heroTitle: v } }))
                          }
                        />
                        <LabeledInput
                          label="Hero Subtitle / 副標題"
                          value={eventManagement.editForm.heroSubtitle}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, heroSubtitle: v } }))
                          }
                        />
                        <LabeledInput
                          label="Venue / 場地"
                          value={eventManagement.editForm.venue}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, venue: v } }))
                          }
                        />
                        <LabeledInput
                          label="Address / 地址"
                          value={eventManagement.editForm.address}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, address: v } }))
                          }
                        />
                        <LabeledInput
                          label="Date Label / 日期"
                          value={eventManagement.editForm.dateLabel}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, dateLabel: v } }))
                          }
                        />
                        <LabeledInput
                          label="Time Label / 時間"
                          value={eventManagement.editForm.timeLabel}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, timeLabel: v } }))
                          }
                        />
                        <LabeledInput
                          label="Organiser Contact / 聯絡方式"
                          value={eventManagement.editForm.organiserContact}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, organiserContact: v },
                            }))
                          }
                        />
                      </div>

                      <LabeledTextarea
                        label="Summary / 摘要"
                        value={eventManagement.editForm.summary}
                        onChange={(v) =>
                          setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, summary: v } }))
                        }
                        rows={4}
                      />
                      <LabeledTextarea
                        label="Member Privilege / 會員禮遇"
                        value={eventManagement.editForm.memberPrivilege}
                        onChange={(v) =>
                          setEventManagement((prev) => ({
                            ...prev,
                            editForm: { ...prev.editForm, memberPrivilege: v },
                          }))
                        }
                        rows={4}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <LabeledTextarea
                          label="Door Policy / 入場時間"
                          value={eventManagement.editForm.doorPolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, doorPolicy: v } }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Age Restriction / 年齡限制"
                          value={eventManagement.editForm.entryAgePolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, entryAgePolicy: v },
                            }))
                          }
                          rows={2}
                        />
                        <LabeledTextarea
                          label="Dress Code / 穿搭指南"
                          value={eventManagement.editForm.dressCode}
                          onChange={(v) =>
                            setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, dressCode: v } }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Cancellation Policy / 取消政策"
                          value={eventManagement.editForm.cancellationPolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, cancellationPolicy: v },
                            }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Transfer Policy / 名額轉讓"
                          value={eventManagement.editForm.transferPolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, transferPolicy: v },
                            }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Weather Policy / 惡劣天氣安排"
                          value={eventManagement.editForm.weatherPolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, weatherPolicy: v },
                            }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Discount Code Policy / 折扣碼須知"
                          value={eventManagement.editForm.discountCodePolicy}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, discountCodePolicy: v },
                            }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Right of Admission / 入場權利"
                          value={eventManagement.editForm.rightOfAdmission}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, rightOfAdmission: v },
                            }))
                          }
                          rows={4}
                        />
                        <LabeledTextarea
                          label="Email Notification Note / 電郵通知備註"
                          value={eventManagement.editForm.emailNotificationNote}
                          onChange={(v) =>
                            setEventManagement((prev) => ({
                              ...prev,
                              editForm: { ...prev.editForm, emailNotificationNote: v },
                            }))
                          }
                          rows={3}
                        />
                      </div>

                      <LabeledTextarea
                        label="Highlights (one per line) / 精彩預告（每行一個）"
                        value={eventManagement.editForm.highlights}
                        onChange={(v) =>
                          setEventManagement((prev) => ({ ...prev, editForm: { ...prev.editForm, highlights: v } }))
                        }
                        rows={8}
                      />

                      {/* Ticket Types */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-gray-800">Ticket Types / 門票類型</label>
                          <button
                            type="button"
                            onClick={() =>
                              setEditTickets((prev) => [
                                ...prev,
                                {
                                  code: "",
                                  name: "",
                                  nameZh: "",
                                  genderLabel: "",
                                  priceHkd: 0,
                                  priceLabel: "",
                                  includes: "",
                                  includesZh: "",
                                  bookingChannel: "",
                                  badge: "",
                                  isActive: true,
                                  soldOut: false,
                                  ticketLimit: 0,
                                  currentSold: 0,
                                },
                              ])
                            }
                            className="text-xs font-semibold text-[#9B72FF] hover:text-[#7C55CC] cursor-pointer"
                          >
                            + Add Ticket / 新增門票
                          </button>
                        </div>
                        {editTickets.length === 0 ? (
                          <p className="text-sm text-gray-400">No ticket types added yet. / 尚未新增門票類型。</p>
                        ) : (
                          <div className="space-y-4">
                            {editTickets.map((t, idx) => {
                              const limitHit = t.ticketLimit > 0 && t.currentSold >= t.ticketLimit;
                              return (
                                <div key={idx} className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                    <div className="flex items-center gap-3 flex-wrap">
                                      <span className="text-sm font-semibold text-gray-700">Ticket {idx + 1}</span>
                                      <span
                                        className={`text-xs px-2 py-1 rounded-full ${
                                          limitHit
                                            ? "bg-red-100 text-red-700 font-semibold"
                                            : t.ticketLimit > 0
                                              ? "bg-blue-50 text-blue-600"
                                              : "bg-gray-100 text-gray-500"
                                        }`}
                                      >
                                        已售出 {t.currentSold || 0}
                                        {t.ticketLimit > 0 ? ` / ${t.ticketLimit}` : " (Unlimited / 不限)"}
                                      </span>
                                      {limitHit ? (
                                        <span className="text-xs font-semibold text-red-600">
                                          ⚠️ 已達上限 Sold Out Locked
                                        </span>
                                      ) : null}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => setEditTickets((prev) => prev.filter((_, i) => i !== idx))}
                                      className="w-6 h-6 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 text-sm cursor-pointer"
                                    >
                                      &times;
                                    </button>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Code / 代碼
                                      </label>
                                      <input
                                        value={t.code}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], code: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                        placeholder="e.g. early-bird"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Name (EN) / 英文名稱
                                      </label>
                                      <input
                                        value={t.name}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], name: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Name (ZH) / 中文名稱
                                      </label>
                                      <input
                                        value={t.nameZh}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], nameZh: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Gender Label / 性別標籤
                                      </label>
                                      <input
                                        value={t.genderLabel}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], genderLabel: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Price (HKD) / 價格
                                      </label>
                                      <input
                                        type="number"
                                        value={t.priceHkd || ""}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], priceHkd: Number(e.target.value) || 0 };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Price Label / 價格標籤
                                      </label>
                                      <input
                                        value={t.priceLabel}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], priceLabel: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Includes (EN) / 包含內容
                                      </label>
                                      <input
                                        value={t.includes}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], includes: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Includes (ZH) / 包含內容中文
                                      </label>
                                      <input
                                        value={t.includesZh}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], includesZh: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Booking Channel / 預訂渠道
                                      </label>
                                      <input
                                        value={t.bookingChannel}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], bookingChannel: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Badge / 徽章
                                      </label>
                                      <input
                                        value={t.badge}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          next[idx] = { ...next[idx], badge: e.target.value };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Ticket Limit / 門票限額
                                        <span className="text-gray-400 ml-1">(0 = Unlimited / 不限)</span>
                                      </label>
                                      <input
                                        type="number"
                                        min={0}
                                        value={t.ticketLimit || ""}
                                        onChange={(e) => {
                                          const next = [...editTickets];
                                          const newLimit = Math.max(0, Number(e.target.value) || 0);
                                          next[idx] = { ...next[idx], ticketLimit: newLimit };
                                          setEditTickets(next);
                                        }}
                                        className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                        placeholder="e.g. 50"
                                      />
                                    </div>
                                    <div className="flex items-end pb-2 gap-4 flex-wrap">
                                      <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                                        <input
                                          type="checkbox"
                                          checked={t.isActive !== false}
                                          onChange={(e) => {
                                            const next = [...editTickets];
                                            next[idx] = { ...next[idx], isActive: e.target.checked };
                                            setEditTickets(next);
                                          }}
                                          className="w-4 h-4 rounded border-gray-300"
                                        />
                                        Active / 啟用
                                      </label>
                                      <label
                                        className={`flex items-center gap-2 text-xs font-medium ${
                                          limitHit ? "text-red-600" : "text-gray-600"
                                        }`}
                                        title={
                                          limitHit
                                            ? "Increase Ticket Limit above current sold count to unlock / 請增加門票限額至高於已售出數量以解鎖"
                                            : ""
                                        }
                                      >
                                        <input
                                          type="checkbox"
                                          checked={limitHit ? true : t.soldOut === true}
                                          disabled={limitHit}
                                          onChange={(e) => {
                                            if (limitHit) return;
                                            const next = [...editTickets];
                                            next[idx] = { ...next[idx], soldOut: e.target.checked };
                                            setEditTickets(next);
                                          }}
                                          className={`w-4 h-4 rounded border-gray-300 ${
                                            limitHit ? "cursor-not-allowed opacity-70" : ""
                                          }`}
                                        />
                                        Sold Out / 已售罄
                                        {limitHit ? (
                                          <span className="text-[10px] text-red-500">(Locked 已鎖定)</span>
                                        ) : null}
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* FAQs */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-semibold text-gray-800">FAQs / 常見問題</label>
                          <button
                            type="button"
                            onClick={() => setEditFaqs((prev) => [...prev, { question: "", answer: "" }])}
                            className="text-xs font-semibold text-[#9B72FF] hover:text-[#7C55CC] cursor-pointer"
                          >
                            + Add FAQ / 新增
                          </button>
                        </div>
                        {editFaqs.length === 0 ? (
                          <p className="text-sm text-gray-400">No FAQs added yet. / 尚未新增常見問題。</p>
                        ) : (
                          <div className="space-y-3">
                            {editFaqs.map((faq, idx) => (
                              <div key={idx} className="flex gap-2 items-start">
                                <div className="flex-1 space-y-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      Question / 問題 {idx + 1}
                                    </label>
                                    <input
                                      value={faq.question}
                                      onChange={(e) => {
                                        const next = [...editFaqs];
                                        next[idx] = { ...next[idx], question: e.target.value };
                                        setEditFaqs(next);
                                      }}
                                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">
                                      Answer / 答案
                                    </label>
                                    <textarea
                                      value={faq.answer}
                                      onChange={(e) => {
                                        const next = [...editFaqs];
                                        next[idx] = { ...next[idx], answer: e.target.value };
                                        setEditFaqs(next);
                                      }}
                                      rows={2}
                                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                                    />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setEditFaqs((prev) => prev.filter((_, i) => i !== idx))}
                                  className="mt-5 w-7 h-7 flex items-center justify-center rounded-full bg-red-50 text-red-500 hover:bg-red-100 text-sm cursor-pointer"
                                >
                                  &times;
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Discount Codes */}
                      <div>
                        <label className="text-sm font-semibold text-gray-800 block mb-3">
                          Discount Codes / 折扣碼
                        </label>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <input
                            type="text"
                            placeholder="Code e.g. STUDENT10"
                            value={newDiscount.code}
                            onChange={(e) => setNewDiscount((prev) => ({ ...prev, code: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-36"
                          />
                          <select
                            value={newDiscount.discountType}
                            onChange={(e) => setNewDiscount((prev) => ({ ...prev, discountType: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm"
                          >
                            <option value="fixed">Fixed (HKD) / 固定金額</option>
                            <option value="percentage">Percentage (%) / 百分比</option>
                          </select>
                          <input
                            type="number"
                            placeholder={newDiscount.discountType === "percentage" ? "e.g. 10" : "e.g. 50"}
                            value={newDiscount.discountValue}
                            onChange={(e) => setNewDiscount((prev) => ({ ...prev, discountValue: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-24"
                          />
                          <input
                            type="number"
                            placeholder="Max uses (0=unlimited)"
                            value={newDiscount.maxUses}
                            onChange={(e) => setNewDiscount((prev) => ({ ...prev, maxUses: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-40"
                          />
                          <button
                            type="button"
                            disabled={discountSaving}
                            onClick={handleAddDiscountCode}
                            className="text-xs font-semibold text-white bg-[#9B72FF] hover:bg-[#7C55CC] px-3 py-2 rounded-xl cursor-pointer disabled:opacity-50"
                          >
                            {discountSaving ? "Saving..." : "Add / 新增"}
                          </button>
                        </div>
                        {discountCodes.length === 0 ? (
                          <p className="text-sm text-gray-400">No discount codes added yet. / 尚未新增折扣碼。</p>
                        ) : (
                          <div className="space-y-2">
                            {discountCodes.map((dc) => (
                              <div
                                key={dc.id}
                                className={`flex items-center justify-between rounded-xl border p-3 ${
                                  dc.isActive ? "bg-gray-50 border-gray-200" : "bg-gray-100 border-gray-100 opacity-60"
                                }`}
                              >
                                <div className="flex items-center gap-3 text-sm">
                                  <span className="font-mono font-semibold text-gray-900">{dc.code}</span>
                                  <span className="text-gray-500">
                                    {dc.discountType === "percentage"
                                      ? `${dc.discountValue}% off`
                                      : `-HKD ${dc.discountValue}`}
                                  </span>
                                  {dc.maxUses > 0 ? (
                                    <span className="text-xs text-gray-400">
                                      ({dc.currentUses}/{dc.maxUses} used)
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">({dc.currentUses} used, unlimited)</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleDiscountCode(dc)}
                                    className={`text-xs font-semibold px-2 py-1 rounded-lg cursor-pointer ${
                                      dc.isActive
                                        ? "bg-green-50 text-green-600 hover:bg-green-100"
                                        : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                    }`}
                                  >
                                    {dc.isActive ? "Active" : "Inactive"}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteDiscountCode(dc)}
                                    className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Custom Fields */}
                      <div>
                        <label className="text-sm font-semibold text-gray-800 block mb-3">
                          Custom Registration Fields / 自訂報名欄位
                        </label>
                        {error ? (
                          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 mb-3 text-sm text-red-700">
                            {error}
                          </div>
                        ) : null}
                        <div className="flex flex-wrap gap-2 mb-3 items-center">
                          <input
                            type="text"
                            placeholder="Field Key e.g. dietary"
                            value={newCustomField.fieldKey}
                            onChange={(e) => {
                              setShowCustomFieldValidation(false);
                              setNewCustomField((prev) => ({ ...prev, fieldKey: e.target.value }));
                            }}
                            className={`border rounded-xl px-3 py-2 text-sm w-36 ${
                              showCustomFieldValidation && !newCustomField.fieldKey.trim()
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-200"
                            }`}
                          />
                          <input
                            type="text"
                            placeholder="Label (EN)"
                            value={newCustomField.labelEn}
                            onChange={(e) => {
                              setShowCustomFieldValidation(false);
                              setNewCustomField((prev) => ({ ...prev, labelEn: e.target.value }));
                            }}
                            className={`border rounded-xl px-3 py-2 text-sm w-28 ${
                              showCustomFieldValidation && !newCustomField.labelEn.trim()
                                ? "border-red-500 focus:ring-red-500"
                                : "border-gray-200"
                            }`}
                          />
                          <input
                            type="text"
                            placeholder="Label (ZH)"
                            value={newCustomField.labelZh}
                            onChange={(e) => setNewCustomField((prev) => ({ ...prev, labelZh: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-28"
                          />
                          <select
                            value={newCustomField.fieldType}
                            onChange={(e) => setNewCustomField((prev) => ({ ...prev, fieldType: e.target.value }))}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm"
                          >
                            <option value="text">Free Text</option>
                            <option value="dropdown">Dropdown</option>
                          </select>
                          <label className="flex items-center gap-1 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={newCustomField.required}
                              onChange={(e) => setNewCustomField((prev) => ({ ...prev, required: e.target.checked }))}
                              className="w-3.5 h-3.5"
                            />
                            Required
                          </label>
                          {customFields.length > 0 ? (
                            <select
                              value={newCustomField.parentFieldKey}
                              onChange={(e) =>
                                setNewCustomField((prev) => ({ ...prev, parentFieldKey: e.target.value }))
                              }
                              className="border border-gray-200 rounded-xl px-3 py-2 text-xs"
                            >
                              <option value="">No parent / 無父欄位</option>
                              {customFields.map((f) => (
                                <option key={f.id} value={f.fieldKey}>
                                  {f.labelEn} ({f.fieldKey})
                                </option>
                              ))}
                            </select>
                          ) : null}
                          {newCustomField.parentFieldKey ? (
                            <input
                              type="text"
                              placeholder="Trigger value e.g. Yes"
                              value={newCustomField.parentValue}
                              onChange={(e) => setNewCustomField((prev) => ({ ...prev, parentValue: e.target.value }))}
                              className="border border-gray-200 rounded-xl px-3 py-2 text-xs w-36"
                            />
                          ) : null}
                          <button
                            type="button"
                            disabled={cfSaving}
                            onClick={handleAddCustomField}
                            className="text-xs font-semibold text-white bg-[#9B72FF] hover:bg-[#7C55CC] px-3 py-2 rounded-xl cursor-pointer disabled:opacity-50"
                          >
                            {cfSaving ? "Saving..." : "Add / 新增"}
                          </button>
                        </div>
                        {newCustomField.fieldType === "dropdown" ? (
                          <div className="mb-3">
                            <label className="text-xs text-gray-500 block mb-1">Dropdown Options (EN / ZH)</label>
                            {newCustomField.options.map((opt, idx) => (
                              <div key={idx} className="flex gap-2 mb-1">
                                <input
                                  type="text"
                                  placeholder="Option EN"
                                  value={opt.en}
                                  onChange={(e) => {
                                    const updated = [...newCustomField.options];
                                    updated[idx] = { ...updated[idx], en: e.target.value };
                                    setNewCustomField((prev) => ({ ...prev, options: updated }));
                                  }}
                                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-32"
                                />
                                <input
                                  type="text"
                                  placeholder="Option ZH"
                                  value={opt.zh}
                                  onChange={(e) => {
                                    const updated = [...newCustomField.options];
                                    updated[idx] = { ...updated[idx], zh: e.target.value };
                                    setNewCustomField((prev) => ({ ...prev, options: updated }));
                                  }}
                                  className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-32"
                                />
                                {idx === newCustomField.options.length - 1 ? (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setNewCustomField((prev) => ({
                                        ...prev,
                                        options: [...prev.options, { en: "", zh: "" }],
                                      }))
                                    }
                                    className="text-xs text-[#9B72FF] hover:underline"
                                  >
                                    + Add option
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setNewCustomField((prev) => ({
                                        ...prev,
                                        options: prev.options.filter((_, i) => i !== idx),
                                      }))
                                    }
                                    className="text-xs text-red-400 hover:underline"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : null}
                        {customFields.length === 0 ? (
                          <p className="text-sm text-gray-400">No custom fields added yet. / 尚未新增自訂欄位。</p>
                        ) : (
                          <div className="space-y-2">
                            {customFields.map((cf) =>
                              editingCfId === cf.id ? (
                                /* Edit mode */
                                <div key={cf.id} className="rounded-xl border border-[#9B72FF] bg-white p-3 space-y-2">
                                  <div className="flex flex-wrap gap-2 items-center">
                                    <input
                                      type="text"
                                      placeholder="Field Key"
                                      value={editCfForm.fieldKey}
                                      onChange={(e) => setEditCfForm((prev) => ({ ...prev, fieldKey: e.target.value }))}
                                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-32"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Label (EN)"
                                      value={editCfForm.labelEn}
                                      onChange={(e) => setEditCfForm((prev) => ({ ...prev, labelEn: e.target.value }))}
                                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-28"
                                    />
                                    <input
                                      type="text"
                                      placeholder="Label (ZH)"
                                      value={editCfForm.labelZh}
                                      onChange={(e) => setEditCfForm((prev) => ({ ...prev, labelZh: e.target.value }))}
                                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-28"
                                    />
                                    <select
                                      value={editCfForm.fieldType}
                                      onChange={(e) =>
                                        setEditCfForm((prev) => ({ ...prev, fieldType: e.target.value }))
                                      }
                                      className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                                    >
                                      <option value="text">Free Text</option>
                                      <option value="dropdown">Dropdown</option>
                                    </select>
                                    <label className="flex items-center gap-1 text-xs text-gray-600">
                                      <input
                                        type="checkbox"
                                        checked={editCfForm.required}
                                        onChange={(e) =>
                                          setEditCfForm((prev) => ({ ...prev, required: e.target.checked }))
                                        }
                                        className="w-3.5 h-3.5"
                                      />
                                      Required
                                    </label>
                                    {customFields.length > 0 ? (
                                      <select
                                        value={editCfForm.parentFieldKey}
                                        onChange={(e) =>
                                          setEditCfForm((prev) => ({ ...prev, parentFieldKey: e.target.value }))
                                        }
                                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs"
                                      >
                                        <option value="">No parent / 無父欄位</option>
                                        {customFields
                                          .filter((f) => f.id !== editingCfId)
                                          .map((f) => (
                                            <option key={f.id} value={f.fieldKey}>
                                              {f.labelEn} ({f.fieldKey})
                                            </option>
                                          ))}
                                      </select>
                                    ) : null}
                                    {editCfForm.parentFieldKey ? (
                                      <input
                                        type="text"
                                        placeholder="Trigger value e.g. Yes"
                                        value={editCfForm.parentValue}
                                        onChange={(e) =>
                                          setEditCfForm((prev) => ({ ...prev, parentValue: e.target.value }))
                                        }
                                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-36"
                                      />
                                    ) : null}
                                  </div>
                                  {editCfForm.fieldType === "dropdown" ? (
                                    <div>
                                      <label className="text-xs text-gray-500 block mb-1">Options (EN / ZH)</label>
                                      {editCfForm.options.map((opt, idx) => (
                                        <div key={idx} className="flex gap-2 mb-1">
                                          <input
                                            type="text"
                                            placeholder="Option EN"
                                            value={opt.en}
                                            onChange={(e) => {
                                              const updated = [...editCfForm.options];
                                              updated[idx] = { ...updated[idx], en: e.target.value };
                                              setEditCfForm((prev) => ({ ...prev, options: updated }));
                                            }}
                                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-32"
                                          />
                                          <input
                                            type="text"
                                            placeholder="Option ZH"
                                            value={opt.zh}
                                            onChange={(e) => {
                                              const updated = [...editCfForm.options];
                                              updated[idx] = { ...updated[idx], zh: e.target.value };
                                              setEditCfForm((prev) => ({ ...prev, options: updated }));
                                            }}
                                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs w-32"
                                          />
                                          {idx === editCfForm.options.length - 1 ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setEditCfForm((prev) => ({
                                                  ...prev,
                                                  options: [...prev.options, { en: "", zh: "" }],
                                                }))
                                              }
                                              className="text-xs text-[#9B72FF] hover:underline"
                                            >
                                              + Add
                                            </button>
                                          ) : (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setEditCfForm((prev) => ({
                                                  ...prev,
                                                  options: prev.options.filter((_, i) => i !== idx),
                                                }))
                                              }
                                              className="text-xs text-red-400 hover:underline"
                                            >
                                              Remove
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                  <div className="flex gap-2">
                                    <button
                                      type="button"
                                      disabled={cfSaving}
                                      onClick={() => handleSaveCustomField(cf)}
                                      className="text-xs font-semibold text-white bg-[#9B72FF] hover:bg-[#7C55CC] px-3 py-1.5 rounded-lg cursor-pointer disabled:opacity-50"
                                    >
                                      {cfSaving ? "Saving..." : "Save / 儲存"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingCfId(null)}
                                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer"
                                    >
                                      Cancel / 取消
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                /* View mode */
                                <div
                                  key={cf.id}
                                  className={`flex items-center justify-between rounded-xl border p-3 ${
                                    cf.isActive
                                      ? "bg-gray-50 border-gray-200"
                                      : "bg-gray-100 border-gray-100 opacity-60"
                                  }`}
                                >
                                  <div className="flex items-center gap-3 text-sm">
                                    <span className="font-mono text-xs text-gray-400">{cf.fieldKey}</span>
                                    <span className="font-medium text-gray-900">
                                      {cf.labelEn && cf.labelZh
                                        ? `${cf.labelEn} / ${cf.labelZh}`
                                        : cf.labelEn || cf.labelZh || ""}
                                      {cf.required ? <span className="text-red-500 ml-1">*</span> : null}
                                    </span>
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 text-gray-600">
                                      {cf.fieldType === "dropdown" ? "Dropdown" : "Text"}
                                    </span>
                                    {cf.required ? (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-50 text-red-500">
                                        Required
                                      </span>
                                    ) : null}
                                    {cf.parentFieldKey ? (
                                      <span className="text-xs text-gray-400">
                                        if [{cf.parentFieldKey}={cf.parentValue || ""}]
                                      </span>
                                    ) : null}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditingCfId(cf.id);
                                        setEditCfForm({
                                          fieldKey: cf.fieldKey,
                                          labelEn: cf.labelEn,
                                          labelZh: cf.labelZh,
                                          fieldType: cf.fieldType,
                                          options: cf.options.length > 0 ? [...cf.options] : [{ en: "", zh: "" }],
                                          required: cf.required,
                                          parentFieldKey: cf.parentFieldKey || "",
                                          parentValue: cf.parentValue || "",
                                        });
                                      }}
                                      className="text-xs font-semibold px-2 py-1 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 cursor-pointer"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleToggleCustomField(cf)}
                                      className={`text-xs font-semibold px-2 py-1 rounded-lg cursor-pointer ${
                                        cf.isActive
                                          ? "bg-green-50 text-green-600 hover:bg-green-100"
                                          : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                                      }`}
                                    >
                                      {cf.isActive ? "Active" : "Inactive"}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteCustomField(cf)}
                                      className="text-xs font-semibold px-2 py-1 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <input
                            type="checkbox"
                            checked={eventManagement.editForm.autoApprove === "true"}
                            onChange={(e) =>
                              setEventManagement((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, autoApprove: String(e.target.checked) },
                              }))
                            }
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          Auto-approve registrations / 自動審核報名
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <input
                            type="checkbox"
                            checked={eventManagement.editForm.isActive !== "false"}
                            onChange={(e) =>
                              setEventManagement((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, isActive: String(e.target.checked) },
                              }))
                            }
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          Is active / 活動啟用
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <input
                            type="checkbox"
                            checked={eventManagement.editForm.showShortCode !== "false"}
                            onChange={(e) =>
                              setEventManagement((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, showShortCode: String(e.target.checked) },
                              }))
                            }
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          Show short code / 顯示簡碼
                        </label>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-800">
                          <input
                            type="checkbox"
                            checked={eventManagement.editForm.registrationOpen !== "false"}
                            onChange={(e) =>
                              setEventManagement((prev) => ({
                                ...prev,
                                editForm: { ...prev.editForm, registrationOpen: String(e.target.checked) },
                              }))
                            }
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          Registration open / 開放報名
                        </label>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button
                          onClick={() => void saveEvent()}
                          disabled={loading}
                          className={`px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-colors ${loading ? "bg-gray-300 cursor-not-allowed" : "bg-gray-900 hover:bg-gray-700"}`}
                        >
                          {loading ? "Saving..." : "Save Event / 儲存活動"}
                        </button>
                        <button
                          onClick={() => {
                            setEventManagement({ editingEvent: null, editForm: {}, isEditing: false });
                            setEditFaqs([]);
                            setEditTickets([]);
                          }}
                          disabled={loading}
                          className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          Cancel / 取消
                        </button>
                      </div>
                    </div>

                    {/* Crop Modal */}
                    {cropImage ? (
                      <EventPhotoCropModal
                        imageDataUrl={cropImage}
                        onConfirm={(croppedDataUrl: string) => {
                          setEventManagement((prev) => ({
                            ...prev,
                            editForm: { ...prev.editForm, photoDataUrl: croppedDataUrl },
                          }));
                          setCropImage(null);
                        }}
                        onCancel={() => setCropImage(null)}
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    {error ? <p className="text-sm text-red-600 mb-4">{error}</p> : null}

                    <div className="overflow-auto border border-gray-100 rounded-2xl">
                      <table className="min-w-[1000px] w-full text-left text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Title / 名稱</th>
                            <th className="px-4 py-3 font-semibold">Slug</th>
                            <th className="px-4 py-3 font-semibold">Date / 日期</th>
                            <th className="px-4 py-3 font-semibold">Pending</th>
                            <th className="px-4 py-3 font-semibold">Approved</th>
                            <th className="px-4 py-3 font-semibold">Auto-approve</th>
                            <th className="px-4 py-3 font-semibold">Active</th>
                            <th className="px-4 py-3 font-semibold">Registration</th>
                            <th className="px-4 py-3 font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {eventSummaries.map((event) => (
                            <tr key={event.id} className="text-gray-800">
                              <td className="px-4 py-3">
                                <div className="font-semibold">{event.titleZh}</div>
                                <div className="text-xs text-gray-500">{event.title}</div>
                              </td>
                              <td className="px-4 py-3 text-xs font-mono">{event.slug}</td>
                              <td className="px-4 py-3">{event.dateLabel}</td>
                              <td className="px-4 py-3">
                                <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                                  {event.pendingReviewCount}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full">
                                  {event.approvedCount}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => toggleEventAutoApprove(event)}
                                  className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    event.autoApprove
                                      ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                  }`}
                                >
                                  {event.autoApprove ? "ON / 開" : "OFF / 關"}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => toggleEventActive(event)}
                                  className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    event.isActive
                                      ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                                  }`}
                                >
                                  {event.isActive ? "Active / 啟用" : "Inactive / 停用"}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <button
                                  onClick={() => toggleEventRegistration(event)}
                                  className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    event.registrationOpen
                                      ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                      : "bg-red-50 text-red-600 hover:bg-red-100"
                                  }`}
                                >
                                  {event.registrationOpen ? "Open / 開放報名" : "Closed / 已截止"}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openEventEditor(event)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                                  >
                                    Edit / 編輯
                                  </button>
                                  <button
                                    onClick={() => exportEvent(event)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                  >
                                    Export / 匯出
                                  </button>
                                  <button
                                    onClick={() => deleteEvent(event)}
                                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                  >
                                    Delete / 刪除
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                          {eventSummaries.length === 0 && !loading ? (
                            <tr>
                              <td className="px-4 py-6 text-gray-500" colSpan={8}>
                                No events found. Create your first event. / 找不到活動，請新增第一個活動。
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Save error Modal */}
      {saveError ? (
        <AlertModal
          title="Unable to Save / 無法儲存"
          subtitle="Please fix the issue below and try again. / 請修正以下問題後重試。"
          message={saveError}
          onClose={() => setSaveError("")}
        />
      ) : null}

      {/* Image upload error Modal */}
      {uploadError ? (
        <AlertModal
          title="Upload Failed / 上傳失敗"
          subtitle="Please choose another image and try again. / 請重新選擇圖片。"
          message={uploadError}
          onClose={() => setUploadError("")}
        />
      ) : null}

      {/* Import Modal */}
      {importModal ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={() => setImportModal(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <p className="font-semibold text-gray-900">Import Event / 匯入活動</p>
              <button
                onClick={() => setImportModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-lg leading-none cursor-pointer"
              >
                &times;
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Slug / 網址代號</label>
                <input
                  value={importSlug}
                  onChange={(e) => setImportSlug(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-900"
                  placeholder="e.g. my-event-2026"
                />
                <p className="text-xs text-gray-400 mt-1">
                  You can change the slug before importing. / 匯入前可修改 slug。
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Event JSON Preview</label>
                <pre className="max-h-60 overflow-auto bg-gray-50 rounded-xl p-4 text-xs text-gray-700 whitespace-pre-wrap">
                  {importJson}
                </pre>
              </div>
              {importError ? (
                <div className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-600">{importError}</div>
              ) : null}
            </div>
            <div className="border-t border-gray-100 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={() => setImportModal(false)}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                Cancel / 取消
              </button>
              <button
                onClick={() => void importEvent()}
                disabled={loading}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gray-900 hover:bg-gray-700 transition-colors cursor-pointer disabled:opacity-40"
              >
                {loading ? "Importing..." : "Import Event / 匯入"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
